import React, { useEffect, useMemo, useState } from "react";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import { useSearchParams, useNavigate } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import {
    GenericTable,
    GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import type { ParsedFilter } from "@ui-components/Searchbar/GenericSearchbar";
import { SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import {
    DetailCardSkeleton,
    ServiceDetailsTableSkeleton,
} from "kontrolEdge/components/Shared/ServiceDetailsPageSkeleton";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import {
    ErrorStateView,
    EmptyStateView,
} from "kontrolEdge/components/Backbone/BackboneDetails/StateViews";
import { useSdwanBranchDetails } from "./useSdwanBranchDetails";
import { fetchSdwanBranchRoutes, fetchSdwanBranchDhcp } from "../requests/request";
import { useCachedFetch } from "@hooks/useCachedFetch";
import type {
    SdwanInternetInterface,
    SdwanRoute,
    SdwanDhcpResponse,
    SdwanDhcpVlan,
} from "../sdwan.types";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import {
    StatusTag,
    OwnershipTag,
    KvRow,
    fmtKbps,
} from "../shared/sdwanFormat";

/** Searchable text per route row (lowercased), keyed by column. */
const routeFields = (r: SdwanRoute): Record<string, string> => ({
    network: (r.network ?? "").toLowerCase(),
    type: (r.type ?? "").toLowerCase(),
    via: (r.interface ?? "").toLowerCase(),
    gateway: (r.gateway ?? "").toLowerCase(),
    distance: r.distance != null ? String(r.distance) : "",
    metric: r.metric != null ? String(r.metric) : "",
});

/**
 * One metric in the DHCP summary strip: an inline `label value` pair inside a
 * soft chip (subtle bg-secondary, rounded, no border/shadow) so each metric
 * reads as its own unit without adding visual weight. Value in the brand token.
 */
const DhcpMetric: React.FC<{
    label: string;
    value: React.ReactNode;
    unit?: string;
}> = ({ label, value, unit }) => (
    <div className="flex items-baseline gap-1.5 rounded-lg bg-semantic-bg-secondary px-3 py-1.5">
        <span className="whitespace-nowrap text-xs font-medium uppercase tracking-wider text-semantic-text-secondary">
            {label}
        </span>
        <span className="whitespace-nowrap text-sm font-bold text-semantic-text-brand">
            {value}
            {unit && (
                <span className="ml-0.5 text-xs font-medium text-semantic-text-secondary">
                    {unit}
                </span>
            )}
        </span>
    </div>
);

/** Status tag + an optional neutral (default) tag, laid out in a row. Used by
 *  the branch Status card for the interface / license-expiry values. */
const StatusTagsRow: React.FC<{
    status: string | null | undefined;
    extra?: string | null;
}> = ({ status, extra }) => (
    <span className="inline-flex items-center gap-2">
        <StatusTag status={status} />
        {extra && (
            <GenericTag variant="default" label={extra} size="small" />
        )}
    </span>
);

const SdwanBranchDetails: React.FC = () => {
    const [searchParams] = useSearchParams();
    const sdwanId = searchParams.get("sdwanId");
    // Catalog id (preferred) or device name (legacy fallback).
    const branchRef = searchParams.get("branchId") ?? searchParams.get("device");
    const navigate = useNavigate();
    const api = useAxiosAuth0();
    const [refresh, setRefresh] = useState(0);
    const { tenantId } = useAppSelector((state) => state.selection);

    const { raw, loading, error } = useSdwanBranchDetails(
        sdwanId,
        branchRef,
        api,
        refresh,
        tenantId ?? null
    );

    // Routing table and DHCP summary — separate live fetches with their own loading
    // state so they never block the main detail card. Cached per branch: coming back
    // (a view switch, Back) paints the last answer while the request refreshes it.
    const branchCacheKey =
        sdwanId && branchRef && tenantId != null
            ? `kontroledge/sdwan/${sdwanId}/branches/${branchRef}?tenantId=${tenantId}`
            : null;
    const [routeFilters, setRouteFilters] = useState<ParsedFilter[]>([]);
    const routesFetch = useCachedFetch<SdwanRoute[]>(
        branchCacheKey && `${branchCacheKey}#routes`,
        async () => {
            // The key is null until these are known, so the hook never calls this without them.
            if (!sdwanId || !branchRef || tenantId == null) return [];
            const res = await fetchSdwanBranchRoutes(api, tenantId, sdwanId, branchRef);
            return res.data?.data?.routes ?? [];
        }
    );
    // A failed fetch reads as an empty table, as before.
    const routes = routesFetch.data ?? (routesFetch.error ? [] : null);
    const routesLoading = routesFetch.isLoading;

    const dhcpFetch = useCachedFetch<SdwanDhcpResponse | null>(
        branchCacheKey && `${branchCacheKey}#dhcp`,
        async () => {
            if (!sdwanId || !branchRef || tenantId == null) return null;
            const res = await fetchSdwanBranchDhcp(api, tenantId, sdwanId, branchRef);
            return res.data?.data ?? null;
        }
    );
    const dhcp = dhcpFetch.data ?? null;
    const dhcpLoading = dhcpFetch.isLoading;

    // "Retry" on the main card re-fetches the side tables too (as when they shared
    // the `refresh` dependency).
    const { reload: reloadRoutes } = routesFetch;
    const { reload: reloadDhcp } = dhcpFetch;
    useEffect(() => {
        if (refresh === 0) return;
        reloadRoutes();
        reloadDhcp();
    }, [refresh, reloadRoutes, reloadDhcp]);

    const internetColumns = useMemo<
        GenericTableColumn<SdwanInternetInterface>[]
    >(
        () => [
            {
                header: "Interface",
                accessor: "port",
                sortable: true,
                renderCell: (row) => (
                    <div className="flex items-center gap-2">
                        <span className="font-medium">{row.port}</span>
                        <OwnershipTag ownership={row.ownership} />
                    </div>
                ),
            },
            {
                header: "Status",
                accessor: "status",
                sortable: true,
                renderCell: (row) => <StatusTag status={row.status} />,
            },
            { header: "IP", renderCell: (row) => row.ip ?? "—" },
            {
                header: "BW out / in",
                renderCell: (row) =>
                    `${fmtKbps(row.bandwidth_kbps.tx)} / ${fmtKbps(row.bandwidth_kbps.rx)}`,
            },
            {
                header: "Internet latency",
                sortAccessor: (row) => row.latency_ms ?? -1,
                sortable: true,
                renderCell: (row) =>
                    row.latency_ms != null
                        ? `${row.latency_ms.toFixed(1)} ms`
                        : "—",
            },
            {
                header: "Hub latency",
                sortAccessor: (row) => row.hub_latency_ms ?? -1,
                sortable: true,
                renderCell: (row) =>
                    row.hub_latency_ms != null
                        ? `${row.hub_latency_ms.toFixed(1)} ms`
                        : "—",
            },
            {
                header: "Jitter",
                sortAccessor: (row) => row.jitter_ms ?? -1,
                sortable: true,
                renderCell: (row) =>
                    row.jitter_ms != null
                        ? `${row.jitter_ms.toFixed(2)} ms`
                        : "—",
            },
            {
                header: "Loss",
                sortAccessor: (row) => row.packet_loss_pct ?? -1,
                sortable: true,
                renderCell: (row) =>
                    row.packet_loss_pct != null
                        ? `${row.packet_loss_pct.toFixed(1)} %`
                        : "—",
            },
        ],
        []
    );

    const routeColumns = useMemo<GenericTableColumn<SdwanRoute>[]>(
        () => [
            {
                header: "Network",
                accessor: "network",
                sortable: true,
                renderCell: (row) => (
                    <span className="font-mono text-xs">{row.network}</span>
                ),
            },
            {
                header: "Type",
                accessor: "type",
                sortable: true,
                renderCell: (row) => (
                    <span className="uppercase text-xs font-medium">
                        {row.type}
                    </span>
                ),
            },
            {
                header: "Via",
                accessor: "interface",
                sortable: true,
                renderCell: (row) => row.interface ?? "—",
            },
            {
                header: "Gateway",
                renderCell: (row) => (
                    <span className="font-mono text-xs">
                        {row.gateway ?? "—"}
                    </span>
                ),
            },
            {
                header: "Distance",
                sortAccessor: (row) => row.distance ?? -1,
                sortable: true,
                renderCell: (row) => row.distance ?? "—",
            },
            {
                header: "Metric",
                sortAccessor: (row) => row.metric ?? -1,
                sortable: true,
                renderCell: (row) => row.metric ?? "—",
            },
        ],
        []
    );

    const dhcpColumns = useMemo<GenericTableColumn<SdwanDhcpVlan>[]>(
        () => [
            {
                header: "Interface / VLAN",
                accessor: "interface",
                sortable: true,
                renderCell: (row) => (
                    <span className="font-mono text-xs">{row.interface}</span>
                ),
            },
            {
                header: "Description",
                accessor: "description",
                sortable: true,
                renderCell: (row) => row.description ?? "—",
            },
            {
                header: "Subnet",
                renderCell: (row) =>
                    row.subnetPrefix != null ? `/${row.subnetPrefix}` : "—",
            },
            {
                header: "In use",
                sortAccessor: (row) => row.inUse,
                sortable: true,
                renderCell: (row) => row.inUse,
            },
            {
                header: "Capacity",
                sortAccessor: (row) => row.capacity ?? -1,
                sortable: true,
                renderCell: (row) => row.capacity ?? "—",
            },
            {
                header: "Util %",
                sortAccessor: (row) => row.utilizationPct ?? -1,
                sortable: true,
                renderCell: (row) =>
                    row.utilizationPct != null
                        ? `${row.utilizationPct}%`
                        : "—",
            },
        ],
        []
    );

    const filteredRoutes = useMemo(() => {
        const rows = routes ?? [];
        if (!routeFilters.length) return rows;
        return rows.filter((r) => {
            const fields = routeFields(r);
            const all = Object.values(fields).join(" ");
            return routeFilters.every((f) => {
                const v = f.value.trim().toLowerCase();
                if (!v) return true;
                const key = f.key?.trim().toLowerCase();
                return key && key in fields
                    ? fields[key].includes(v)
                    : all.includes(v);
            });
        });
    }, [routes, routeFilters]);

    if (loading) {
        return (
            <div
                className="flex flex-col gap-4 py-4"
                role="status"
                aria-busy
                aria-label="Loading SD-WAN branch details"
            >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <DetailCardSkeleton />
                    <DetailCardSkeleton />
                </div>
                <ServiceDetailsTableSkeleton
                    variant="data"
                    rows={4}
                    columns={7}
                />
                <ServiceDetailsTableSkeleton
                    variant="data"
                    rows={3}
                    columns={7}
                />
                <ServiceDetailsTableSkeleton
                    variant="data"
                    rows={5}
                    columns={6}
                />
            </div>
        );
    }
    if (error)
        return (
            <ErrorStateView
                title="Failed to load SD-WAN branch"
                message={error}
                onRetry={() => setRefresh((r) => r + 1)}
            />
        );
    if (!raw)
        return (
            <EmptyStateView
                title="SD-WAN branch not found"
                description="We couldn't load this branch. It may have been removed or you may not have access."
            />
        );

    const d = raw.details;
    const s = raw.status;

    const goHub = (hubId: number) =>
        navigate(buildDetailPath("sdwanHub", { sdwanId: hubId }));

    return (
        <div className="flex flex-col gap-4">
            {/* Back navigation is provided by the SideNav (getBackRoute). */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <GenericCard isCollapsible={false} variant="primary" shadow="sm">
                    <div className="p-5">
                        <h3 className="font-semibold mb-3">Branch details</h3>
                        {d.service_id != null && (
                            <KvRow label="Service ID">{d.service_id}</KvRow>
                        )}
                        <KvRow label="Hostname">{d.hostname}</KvRow>
                        <KvRow label="Model">{d.model ?? "—"}</KvRow>
                        <KvRow label="FortiOS">
                            {d.fortios
                                ? `${d.fortios}${d.build ? ` (build ${d.build})` : ""}`
                                : "—"}
                        </KvRow>
                        <KvRow label="Serial">
                            <span className="font-mono text-xs">
                                {d.serial ?? "—"}
                            </span>
                        </KvRow>
                        {d.location != null && (
                            <KvRow label="Location">{d.location}</KvRow>
                        )}
                        <KvRow
                            label={
                                d.connected_hubs.length > 1
                                    ? "Connected hubs"
                                    : "Connected hub"
                            }
                        >
                            {d.connected_hubs.length ? (
                                <div className="flex flex-col items-end gap-1">
                                    {d.connected_hubs.map((h) => (
                                        <button
                                            key={h.id}
                                            {...viewHref(buildDetailPath("sdwanHub", { sdwanId: h.id }))}
                                            className="hover:underline text-semantic-text-brand font-medium"
                                            onClick={() =>
                                                goHub(h.id)
                                            }
                                        >
                                            {h.label}
                                            {h.location && (
                                                <span className="ml-2 text-xs text-semantic-text-tertiary">
                                                    {h.location}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                "—"
                            )}
                        </KvRow>
                        <KvRow label="HA">
                            {d.ha.mode === "standalone"
                                ? "Standalone"
                                : d.ha.mode === "a-p"
                                  ? "Active-Passive"
                                  : "Active-Active"}
                        </KvRow>
                    </div>
                </GenericCard>

                <GenericCard isCollapsible={false} variant="primary" shadow="sm">
                    <div className="p-5">
                        <h3 className="font-semibold mb-3">Status</h3>
                        <KvRow label="Operational status">
                            <StatusTag status={s.operational} />
                        </KvRow>
                        <KvRow
                            label={
                                <span className="flex flex-col">
                                    <span>Internet primary</span>
                                    <span className="text-xs text-semantic-text-tertiary">
                                        Telecom District
                                    </span>
                                </span>
                            }
                        >
                            {s.internet_primary ? (
                                <StatusTagsRow
                                    status={s.internet_primary.status}
                                    extra={s.internet_primary.interface}
                                />
                            ) : (
                                "—"
                            )}
                        </KvRow>
                        <KvRow
                            label={
                                <span className="flex flex-col">
                                    <span>Internet secondary</span>
                                    <span className="text-xs text-semantic-text-tertiary">
                                        External ISP
                                    </span>
                                </span>
                            }
                        >
                            {s.internet_secondary ? (
                                <StatusTagsRow
                                    status={s.internet_secondary.status}
                                    extra={s.internet_secondary.interface}
                                />
                            ) : (
                                "—"
                            )}
                        </KvRow>
                        <KvRow label="License">
                            {s.license ? (
                                <StatusTagsRow
                                    status={s.license.status}
                                    extra={
                                        s.license.expires
                                            ? `exp ${s.license.expires}`
                                            : null
                                    }
                                />
                            ) : (
                                "—"
                            )}
                        </KvRow>
                    </div>
                </GenericCard>
            </div>

            <GenericCard isCollapsible={false} variant="primary" shadow="sm">
                <div className="p-5">
                    <h3 className="font-semibold">Internet interfaces</h3>
                    <p className="text-sm text-semantic-text-secondary mb-3">
                        Internet WAN links — live status, SLA & bandwidth
                    </p>
                    <GenericTable
                        columns={internetColumns}
                        data={raw.internet_interfaces}
                        initialRowsPerPage={10}
                        rowsPerPageOptions={[10, 20]}
                        width="100%"
                        tableClassName="bg-semantic-bg-primary"
                    />
                </div>
            </GenericCard>

            <GenericCard isCollapsible={false} variant="primary" shadow="sm">
                <div className="p-5">
                    <h3 className="font-semibold">DHCP summary</h3>
                    <p className="text-sm text-semantic-text-secondary mb-3">
                        Connected devices &amp; network capacity — live DHCP
                        leases per VLAN
                    </p>
                    {dhcpLoading ? (
                        <SkeletonBlock className="h-64" />
                    ) : dhcp ? (
                        <>
                            <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-semantic-border-secondary pb-4">
                                <DhcpMetric
                                    label="Connected devices"
                                    value={dhcp.summary.connectedDevices.toLocaleString(
                                        "en-US"
                                    )}
                                />
                                <DhcpMetric
                                    label="Unique MACs"
                                    value={dhcp.summary.uniqueMacs.toLocaleString(
                                        "en-US"
                                    )}
                                />
                                <DhcpMetric
                                    label="Reserved"
                                    value={dhcp.summary.reserved}
                                />
                                <DhcpMetric
                                    label="Dynamic"
                                    value={dhcp.summary.dynamic}
                                />
                                <DhcpMetric
                                    label="Network capacity"
                                    value={
                                        dhcp.summary.totalCapacity != null
                                            ? dhcp.summary.totalCapacity.toLocaleString(
                                                  "en-US"
                                              )
                                            : "—"
                                    }
                                    unit={
                                        dhcp.summary.totalCapacity != null
                                            ? "hosts"
                                            : undefined
                                    }
                                />
                                <DhcpMetric
                                    label="Utilization"
                                    value={
                                        dhcp.summary.utilizationPct != null
                                            ? `${dhcp.summary.utilizationPct}%`
                                            : "—"
                                    }
                                />
                            </div>
                            <GenericTable
                                columns={dhcpColumns}
                                data={dhcp.vlans}
                                initialRowsPerPage={10}
                                rowsPerPageOptions={[10, 20]}
                                width="100%"
                                tableClassName="bg-semantic-bg-primary"
                            />
                        </>
                    ) : (
                        <p className="text-sm text-semantic-text-secondary">
                            No DHCP data available
                        </p>
                    )}
                </div>
            </GenericCard>

            <GenericCard isCollapsible={false} variant="primary" shadow="sm">
                <div className="p-5">
                    <h3 className="font-semibold">Routing table</h3>
                    <p className="text-sm text-semantic-text-secondary mb-3">
                        Routes installed on the branch — BGP routes are learned
                        from the hub over the overlay tunnels
                    </p>
                    <div className="flex items-center gap-4 mb-4">
                        <div className="w-[520px] max-w-full">
                            <GenericSearchbar
                                placeholder="Find by network, type, via, gateway, distance, metric…"
                                inputMaxWidth="100%"
                                suggestedKeys={[
                                    "network",
                                    "type",
                                    "via",
                                    "gateway",
                                    "distance",
                                    "metric",
                                ]}
                                onFiltersChange={setRouteFilters}
                            />
                        </div>
                    </div>
                    {routesLoading ? (
                        <SkeletonBlock className="h-64" />
                    ) : (
                        <GenericTable
                            columns={routeColumns}
                            data={filteredRoutes}
                            initialRowsPerPage={10}
                            rowsPerPageOptions={[10, 20, 50]}
                            width="100%"
                            tableClassName="bg-semantic-bg-primary"
                        />
                    )}
                </div>
            </GenericCard>
        </div>
    );
};

export default SdwanBranchDetails;
